<template>
  <div>
    <loading :active.sync="showLoading" :can-cancel="false" :opacity="0.7">
      <div slot="after" class="after-icon">
        <h3>
          {{ lang.get('payment-subscriptions.messages.payment-in-progress') }}
        </h3>
      </div>
    </loading>
    <transition name="transition-errors">
      <div
        v-if="ajaxErrorMessages.length"
        class="alert-error sticky island"
        role="alert"
        tabindex="-1"
      >
        <div class="icon">
          <div class="af-icons-md af-icons-md-alert-error"></div>
        </div>
        <div class="message">
          <p
            v-html="lang.get('payment-subscriptions.errors.toaster-message')"
          />
          <ul class="errors">
            <li v-for="error in ajaxErrorMessages" :key="error">{{ error }}</li>
          </ul>
        </div>
      </div>
    </transition>
    <form
      ref="form"
      method="post"
      :target="target"
      @submit.stop.prevent="submitForm"
    >
      <div class="row">
        <div class="col-md-4">
          <div class="form-group panel panel-default panel-body">
            <h5 class="strong">
              {{ lang.get('payment-subscriptions.titles.plans') }}
            </h5>
            <div
              v-for="plan in plans"
              :key="plan"
              class="form-group-sm radio styled"
            >
              <input
                :id="plan"
                v-model="selectedPlan"
                type="radio"
                name="plan"
                :value="plan"
              />
              <label :for="plan">{{
                lang.get('payment-subscriptions.form.plan-' + plan + '.label')
              }}</label>
            </div>
            <div class="form-group">
              <a
                :href="comparePlansUrl"
                rel="noopener noreferrer"
                target="_blank"
                >{{ lang.get('payment-subscriptions.titles.compare-plans') }}</a
              >
            </div>
          </div>

          <div class="form-group panel panel-default panel-body">
            <h5 class="strong">
              {{ lang.get('payment-subscriptions.titles.payment-options') }}
            </h5>
            <div
              v-for="(paymentOptionData, paymentOption) in paymentOptions"
              :key="paymentOption"
              class="form-group-sm radio styled"
            >
              <input
                :id="paymentOption"
                v-model="selectedOption"
                type="radio"
                name="paymentOption"
                :value="paymentOption"
              />
              <label :for="paymentOption" v-html="paymentOptionData['label']">
              </label>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="panel panel-default panel-body">
            <h5 class="strong">
              {{ lang.get('payment-subscriptions.titles.organisation') }}
            </h5>

            <div class="form-group">
              <label for="billingName">{{
                lang.get('payment-subscriptions.form.billing-name.label')
              }}</label>
              <input
                id="billingName"
                type="text"
                name="billingName"
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label for="country">{{
                lang.get('ecommerce.cart.form.country.label')
              }}</label
              ><br />
              <select
                id="country"
                v-model="selectedCountry"
                name="country"
                class="form-control"
              >
                <option
                  v-for="(country, key) in countries"
                  :key="key"
                  :value="key"
                  >{{ country }}</option
                >
              </select>
            </div>

            <div class="form-group">
              <label for="address">{{
                lang.get('ecommerce.cart.form.street-address.label')
              }}</label>
              <input
                id="address"
                type="text"
                name="address"
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label for="city">{{
                lang.get('ecommerce.cart.form.city.label')
              }}</label>
              <input id="city" type="text" name="city" class="form-control" />
            </div>

            <div v-if="!showRegion" class="form-group">
              <label for="state" class="text-capitalize">{{
                lang.get('terms-service.state.singular')
              }}</label>
              <input id="state" type="text" name="state" class="form-control" />
            </div>

            <div v-if="showRegion" class="form-group">
              <region-selector
                :regions="regions"
                :country="selectedCountry"
                :language="language"
                :default-language="defaultLanguage"
                :translations="translations"
              />
            </div>

            <div class="form-group">
              <label for="postcode" class="text-capitalize">{{
                lang.get('terms-service.postcode.singular')
              }}</label>
              <input
                id="postcode"
                type="text"
                name="postcode"
                class="form-control"
              />
            </div>

            <div v-if="showVatInput" class="form-group">
              <label for="vatNumber">{{
                lang.get('ecommerce.cart.form.vatNumber.label')
              }}</label>
              <input
                id="vatNumber"
                v-model="vatNumber"
                type="text"
                name="vatNumber"
                class="form-control"
              />
              <span class="help-block">{{
                lang.get('payment-subscriptions.form.vat-number.help')
              }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="panel panel-default panel-body">
            <div class="form-group">
              <h5 class="strong">
                {{ lang.get('payment-subscriptions.titles.subscription-cost') }}
              </h5>

              <div
                v-for="cur in currencies"
                :key="cur"
                class="form-group radio radio-inline styled"
              >
                <input
                  :id="cur"
                  v-model="currency"
                  type="radio"
                  name="currency"
                  :value="cur"
                  :disabled="lockCurrency !== '' && cur !== lockCurrency"
                />
                <label :for="cur">{{ cur.toUpperCase() }}</label>
              </div>
            </div>

            <div id="subscription-cost" class="form-group">
              <div :key="selectedOption" class="payment-interval">
                {{ interval }}
              </div>
              <div :key="currency" class="payment-price">
                {{ price }}<span class="price-decimals"></span>
              </div>
              <div v-if="!vatApplicable" class="payment-vat-not-applicable">
                {{ lang.get('payment-subscriptions.form.vat-not-applicable') }}
              </div>
              <div v-if="vatApplicable" class="payment-vat-not-applicable">
                {{ lang.get('payment-subscriptions.form.vat-applicable') }}
              </div>
            </div>
          </div>

          <stripe-card
            :stripe-key="stripeKey"
            :show-card-form="showCardForm"
            :translations="translations"
            :default-language="defaultLanguage"
            :language="language"
            :three-d-secure="threeDSecure"
            :three-d-secure-data="threeDSecureData"
            :clear-card-form="clearCardForm"
            @cardToken="cardTokenReady"
            @cardTokenError="cardTokenError"
            @paymentIntentError="paymentIntentError"
            @paymentIntentSuccess="paymentIntentSuccess"
            @resetCardToken="resetCardToken"
          />
        </div>

        <div class="col-md-12">
          <input
            type="submit"
            class="btn btn-primary btn-lg"
            :disabled="!submitButtonEnabled"
            :value="lang.get('payment-subscriptions.form.submit')"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import langMixin from '../../../lib/components/Translations/mixins/lang-mixin.js';
import CountryField from '../../../lib/components/Fields/CountryField';
import StripeCard from '../../../lib/components/StripeCard';
import getLocale from '../../../lib/locale';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import RegionSelector from '../../../lib/components/RegionSelector';

const $ = require('jQuery');

export default {
  components: { RegionSelector, StripeCard, CountryField, Loading },
  mixins: [langMixin],
  props: {
    plans: {
      type: Object,
      required: true
    },
    comparePlansUrl: {
      type: String,
      required: true
    },
    currentAccountPlan: {
      type: String,
      required: true
    },
    paymentOptions: {
      type: Object,
      required: true
    },
    target: {
      type: String,
      required: true
    },
    defaultCurrency: {
      type: String,
      required: true
    },
    lockCurrency: {
      type: String,
      default: null
    },
    product: {
      type: Object,
      required: true
    },
    countries: {
      type: Object,
      required: true
    },
    regions: {
      type: Object,
      required: true
    },
    defaultCountry: {
      type: String,
      required: true
    },
    euCountries: {
      type: Object,
      required: true
    },
    products: {
      type: Object,
      required: true
    },
    stripeKey: {
      type: String,
      required: true
    },
    successUrl: {
      type: String,
      required: true
    },
    verifyUrl: {
      type: String,
      required: true
    }
  },
  data() {
    const option = Object.values(this.paymentOptions).findIndex(function(
      option
    ) {
      return option.selected;
    });

    return {
      currency:
        this.lockCurrency !== '' ? this.lockCurrency : this.defaultCurrency,
      selectedCountry: this.defaultCountry,
      selectedPlan: this.currentAccountPlan,
      selectedOption: Object.keys(this.paymentOptions)[option],
      vatNumber: '',
      ajaxErrorMessages: [],
      threeDSecure: false,
      threeDSecureData: {},
      clearCardForm: false,
      showLoading: false,
      cardToken: null,
      currencies: ['aud', 'cad', 'eur', 'gbp', 'nzd', 'sgd', 'usd']
    };
  },
  computed: {
    showVatInput: function() {
      return this.euCountries[this.selectedCountry] !== undefined;
    },

    showRegion: function() {
      return this.regions[this.selectedCountry] !== undefined;
    },
    price: function() {
      let product = this.products[
        this.currency +
          '-' +
          this.selectedOption.slice(0, 1) +
          '-' +
          this.selectedPlan +
          '-2'
      ];

      if (product === undefined) {
        product = this.products[
          this.currency +
            '-' +
            this.selectedOption.slice(0, 1) +
            '-' +
            this.selectedPlan +
            '-1'
        ];
      }
      let displayPrice = product.price;

      if (this.vatApplicable) {
        displayPrice *= 1.18;
      }

      return this.formatPrice(displayPrice, this.currency);
    },

    vatApplicable: function() {
      if (this.selectedCountry === 'MT') {
        return true;
      }
      return (
        this.selectedCountry.length &&
        this.euCountries[this.selectedCountry] !== undefined &&
        this.vatNumber.length < 8
      );
    },

    showCardForm: function() {
      return this.selectedOption.split('-', 2)[1] === 'credit';
    },
    interval: function() {
      const interval = this.selectedOption.split('-')[0];
      return this.lang.get('payment-subscriptions.form.intervals.' + interval);
    },
    submitButtonEnabled: function() {
      if (this.showCardForm) {
        return this.cardToken !== null;
      }

      return true;
    }
  },
  watch: {
    cardToken: function(status) {
      if (status) {
        this.ajaxErrorMessages = [];
      }
    }
  },
  methods: {
    formatPrice(price, currency) {
      const formatter = new Intl.NumberFormat(this.determineLocale(), {
        style: 'currency',
        currency: currency
      });

      return formatter.format(price);
    },
    cardTokenReady: function(token) {
      this.cardToken = token;
    },
    cardTokenError: function(error) {
      this.ajaxErrorMessages.push(error);
    },
    paymentIntentError: function(error) {
      this.hideLoadingOverlay();
      this.ajaxErrorMessages.push(error);
    },
    paymentIntentSuccess: function(response) {
      this.$http
        .post(this.verifyUrl, { paymentId: response.paymentIntent.id })
        .then(() => {
          this.redirectSuccess();
        })
        .catch(err => {
          if (err.response) {
            this.ajaxErrorMessages = Object.values(err.response.data).flatten();
          }
        });
    },
    submitForm: function() {
      this.showLoadingOverlay();
      this.resetForm();
      this.$http
        .post(this.target, $(this.$refs['form']).serialize())
        .then(response => {
          if (response.data.success) {
            this.redirectSuccess();
          }

          if (
            response.data['action'] !== undefined &&
            response.data['action'] === '3dsecure'
          ) {
            this.threeDSecureData = response.data;
            this.threeDSecure = true;
          }
        })
        .catch(err => {
          if (err.response) {
            this.ajaxErrorMessages = Object.values(err.response.data).flatten();
            this.clearCardInputs();
          }

          this.hideLoadingOverlay();
        });

      this.clearCardForm = false;
    },
    resetForm: function() {
      this.ajaxErrorMessages = [];
      this.clearCardInputs();
    },

    clearCardInputs: function() {
      this.threeDSecure = false;
      this.threeDSecureData = {};
      this.clearCardForm = true;
      this.resetCardToken();
    },

    resetCardToken: function() {
      this.cardToken = null;
    },

    redirectSuccess: function() {
      window.location.href = this.successUrl;
    },

    determineLocale: function() {
      const currentLanguage = Object.keys(this.lang.messages)[0].split('_')[0];

      return getLocale(this.selectedCountry, currentLanguage);
    },

    showLoadingOverlay: function() {
      this.showLoading = true;
    },

    hideLoadingOverlay: function() {
      this.showLoading = false;
    }
  }
};
</script>

<style scoped>
.payment-price {
  font-size: 24px;
  font-weight: bold;
  line-height: 32px;
}
.transition-errors-enter,
.transition-errors-leave-to {
  height: 0;
  margin: 0;
  padding: 0;
  opacity: 0;
}

.transition-errors-enter-active,
.transition-errors-leave-active {
  transition: all 0.3s;
}
</style>
